import { EventEmitter, Injectable, Output } from '@angular/core';
import { Observable } from 'rxjs';
import * as _ from 'lodash';

import { SearchService } from 'shared/search/search.service';
import { Config } from 'shared/config/env.config';
import { SearchModel } from 'shared/search/search.model';
import { ActiveFilterModel } from 'shared/search/active-filters/active-filter.model';
import { MY_FITMENTS } from 'shared/search/search-service-types';
import { getFiltersQueryString, getVcdbFiltersQueryString } from 'shared/search/search-helpers';
import { VCDB_FILTER_TYPE } from 'shared/search/active-filters/active-filter-types';
import { YEAR_START_TYPE, YEAR_END_TYPE } from 'shared/search/advanced-search/filter-types';
import { statusLookup } from './my-fitments-statuses';
import { validationToColumns } from './validation-to-columns';
import {of} from 'rxjs/internal/observable/of';
import {catchError, map} from 'rxjs/operators';


@Injectable()
export class MyFitmentsSearchService extends SearchService {

  // extendable fields:
  searchModel: SearchModel = new SearchModel();
  serviceType = MY_FITMENTS;
  idField: string = 'fitmentItemId';

  statusLookup: Array<any> = statusLookup;
  baseVehicleColumns: Array<string> = [
    'make', 'model', 'year', 'sub_model'
  ];
  protectedColumns: Array<string> = [];

  @Output() updateColumnsEvent: EventEmitter<any> = new EventEmitter();
  @Output() dataLoadedEvent: EventEmitter<any> = new EventEmitter();

  getSearchURI(brandId: string, searchModel: SearchModel): string {
    if (this.listFilterId) {
      return `/fitments/${brandId}/fitment-list/${this.listFilterId}`;
    }
    if (searchModel.productId) {
      return `/fitments/${brandId}/item-detail/${searchModel.productId.toString()}`;
    }
    return `/fitments/index`;
  }

  getQueryString(searchModel: SearchModel): string {
    let sortCol: string = _.get(searchModel, 'sortModel.column', '');
    let sortDir: string = _.get(searchModel, 'sortModel.sort', '');
    if (sortCol) {
      const columns = _.concat(['year', 'make', 'model'], searchModel.columns);
      // reset sorting if sortCol is not exist in the columns
      if (!_.includes(columns, searchModel.sortModel.column)) {
        sortCol = '';
        sortDir = '';
        searchModel.sortModel = null;
      }
    }
    return 'page=' + searchModel.currentPage +
      '&itemsPerPage=' + searchModel.itemsPerPage +
      '&sortCol=' + sortCol +
      '&sortDir=' + sortDir +
      this.getStatusString(searchModel) +
      this.getColumnsQueryString(searchModel.columns) +
      getFiltersQueryString(this.vehicleFilters) +
      getVcdbFiltersQueryString(this.vcdbFilters);
  }

  getStatusString(searchModel: SearchModel): string {
    const status = _.get(searchModel, 'statusModel.id', '').toString();
    return (status === '2') ? `&hasDuplicates=${1}` : `&status=${status}`;
  }

  runSearch(brandId: string, searchModel: SearchModel, ...args: any[]): Observable<any> {
    if (!searchModel.filters.length && !searchModel.productId) {
      const data: any = {data: [], total: 0, current_page: 1};
      this.searchResults = data.data;
      this.totalRecords = data.total;
      this.searchModel.total = data.total;
      this.searchModel.currentPage = data.current_page;
      return of(data);
    }
    return super.runSearch(brandId, searchModel, args);
  }

  updateData(data: any) {
    this.createUniqId(data);
    // add indicator for properties which should be hightlighted in search results
    if (this.listFilterId || this.searchModel.productId) {
      this.setHighlightedColumns(data.data);
      this.checkBaseVehicleValidity(data.data);
    }
    if (data.columns) {
      this.updateColumnsEvent.emit(data.columns);
    }
    this.protectedColumns = _.get(data, 'protected_columns', []);
    super.updateData(data);
    this.dataLoadedEvent.emit(data);
  }

  /*
    * Mark columns that needs to be highlighted
   */
  setHighlightedColumns(data: any): void {
    _.each(data, (item: any) => {
      if (item.status === 1) {
        // mark column with position error as red
        const positionError = _.get(item, `validation.pcdb.${item.assignment_values.item_detail_id}.position`);
        if (positionError) {
          _.set(item, `highlightedColumns.${positionError.segment}.color`, 'red');
        }
        // check vcdb errors
        const validation = _.get(item, 'validation.vcdb', []);
        if (!validation.length) { return; }
        _.each(_.filter(validation, ['error_type', 'audit']), (errorItem: any) => {
          // Check if columns are in the validaiton group
          if (validationToColumns[errorItem.segment]) {
            _.each(validationToColumns[errorItem.segment], (columnName: any) => {
              _.set(item, `highlightedColumns.${columnName}.color`, 'red');
            });
          } else {
            _.set(item, `highlightedColumns.${errorItem.segment}.color`, 'red');
          }
        });
        _.set(item, 'audit_combined_error', _.some(validation, ['error_type', 'audit_combined']));

      }
    });
  }

  createUniqId(data: any): void {
    // create uniq fitmentItemId for properly select, remove fitments
    // base_behicle_id isn't uniq
    if (this.listFilterId || this.searchModel.productId) {
      _.each(data.data, (item: any) => {
        item.fitmentItemId = item.assignment_values.app_id;
      });
    } else {
      _.each(data.data, (item: any) => {
        item.fitmentItemId = _.values(item.assignment_values).join('');
      });
    }
  }

  checkBaseVehicleValidity(data: Array<any>) {
    _.each(data, (item: any) => {
      if (this.isBaseVehicleInvalid(item)) {
        // Indicate not validity to prevent researching
        item.invalidBaseVehicle = true;
      }
    });
  }

  isBaseVehicleInvalid(item: any): boolean {
    // invalid if year/make/model/submodel are invalid
    return _.some(_.get(item, 'validation.vcdb'), ['segment', 'base_vehicle']) &&
     !_.some(_.get(item, 'validation.vcdb'), ['error_type', 'audit_combined']);
  }

  runResearch(brandId: string, searchQueryString: string): Observable<any> {
    return this.http.get(`${Config.API}/fitments/index?${searchQueryString}`)

      .pipe(map((res: any) => {
        _.each(res.data, (item: any) => {
          item[this.idField] = _.values(item.assignment_values).join('');
        });
        return res;
      }),
      catchError(this.handleError.bind(this)));
  }
}
